import { CloseButton } from '@components/utility/buttons';
import { InputField } from '@components/utility/inputs';
import { Select } from '@components/utility/selects';
import { FailedStatus } from '@event/components/display/dialogs';
import { GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { useAddNonParticipantMutation, usePatchUserPrimaryDetailsMutation } from '@event/services/events';
import { EventTypes, IEventUser, WaitingListProps } from '@event/types/event';
import { renderCountryOption, renderCountryValue } from '@event/utils';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, CircularProgress, Dialog, DialogContent, DialogProps, DialogTitle, Typography } from '@mui/material';
import getUnauthenticatedFormSchema from '@schemas/UnauthenticatedForm';
import { useGetCountriesQuery } from '@services/countries';
import { handleError, toaster } from '@utils/common';
import { toLogin } from '@utils/route';
import { isApiResponseError } from '@utils/service';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

export type UnauthenticatedMode = 'unauthenticated' | 'missing-details' | 'hybrid';

interface Props extends DialogProps {
    setFailedStatus: React.Dispatch<React.SetStateAction<FailedStatus | undefined>>;
    handleUnauthQuizz: (values?: {
        first_name: string;
        last_name: string;
        email: string;
        company_name?: string;
        country_code?: string;
    }) => void;
    mode?: UnauthenticatedMode;
    handleRegister: () => Promise<void>;
    handleJoinWaitingList: (
        formValues?: Omit<WaitingListProps, 'type' | 'id' | 'colleagueHash' | 'guestAccessHashParam'>
    ) => Promise<void>;
    handleHybridRegistration: (formValues: {
        email: string;
        first_name: string;
        last_name: string;
        company_name?: string;
    }) => void;
    isWaitingListFlow?: boolean;
    eventId?: string;
    eventType?: EventTypes;
    setSearchParams: (params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams)) => void;
}

const UnauthenticatedFormDialog = ({
    setFailedStatus,
    handleUnauthQuizz,
    mode,
    handleRegister,
    handleJoinWaitingList,
    handleHybridRegistration,
    isWaitingListFlow,
    eventId,
    eventType,
    setSearchParams,
    ...props
}: Props) => {
    const [isRegistrationLoading, setIsRegistrationLoading] = useState(false);
    const {
        event: { data: event },
        user: { data: user },
        guestUserInfo: { data: guestUserInfo },
        guestAccessHash,
    } = usePublicEventContext();
    const authUser = guestUserInfo || user;
    const intl = useIntl();

    const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();

    const [addNonParticipant, { isLoading: isAddNonParticipantLoading }] = useAddNonParticipantMutation();
    const [patchUserPrimaryDetails, { isLoading: isPatchUserPrimaryDetailsLoading }] =
        usePatchUserPrimaryDetailsMutation();
    const isLoading = isRegistrationLoading || isAddNonParticipantLoading || isPatchUserPrimaryDetailsLoading;

    const [initialValues, setInitialValues] = useState<
        Pick<IEventUser, 'company_name' | 'email'> & { first_name: string; last_name: string; country_code?: string }
    >({
        first_name: '',
        last_name: '',
        email: '',
        company_name: '',
        country_code: '',
    });

    // Update initialValues only when modal opens
    useEffect(() => {
        if (props.open) {
            const values =
                mode === 'missing-details' && authUser
                    ? {
                          first_name: authUser.first_name || '',
                          last_name: authUser.last_name || '',
                          email: authUser.email || '',
                          company_name: authUser.company_name || '',
                          country_code: authUser.country_id || undefined,
                      }
                    : initialValues;
            setInitialValues(values);
            resetForm({
                values,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, mode, authUser]);

    const { handleSubmit, errors, touched, resetForm, values, handleChange, handleBlur } = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: getUnauthenticatedFormSchema(intl),
        onSubmit: async (values) => {
            try {
                if (mode === 'unauthenticated') {
                    if (isWaitingListFlow) {
                        const body: Omit<WaitingListProps, 'type' | 'id' | 'colleagueHash' | 'guestAccessHashParam'> = {
                            email: values.email,
                            company_name: values.company_name ?? '',
                            first_name: values.first_name,
                            last_name: values.last_name,
                        };

                        if (!!values.country_code) {
                            body.country_code = values.country_code;
                        }
                        // Call parent function with form values
                        await handleJoinWaitingList(body);
                        handleClose();
                        return;
                    }

                    // For regular registration, proceed with addNonParticipant logic
                    // First call this with commit=false
                    try {
                        delete values.country_code;
                        await addNonParticipant({
                            id: Number(eventId),
                            type: eventType as EventTypes,
                            commit: false,
                            ...values,
                        }).unwrap();
                    } catch (err) {
                        if (isApiResponseError(err) && err.data && err.data.email?.[0]) {
                            setFailedStatus(err.data.email[0] as FailedStatus);
                            handleClose();
                            return;
                        }
                    }

                    // If commit=false passes
                    // Will registration include a questionnaire?
                    if (event?.enable_questionnaire) {
                        handleClose();
                        handleUnauthQuizz(values);
                    } else {
                        const { guest_access_hash } = await addNonParticipant({
                            id: Number(eventId),
                            type: eventType as EventTypes,
                            ...values,
                        }).unwrap();
                        toaster.success({
                            msg: intl.formatMessage({
                                id: 'event.registered',
                                defaultMessage: 'You are now registered',
                            }),
                        });
                        setSearchParams((prev) => {
                            const params = new URLSearchParams(prev);
                            params.set(GUEST_ACCESS_HASH_KEY, guest_access_hash);
                            return params;
                        });
                    }
                    handleClose();
                } else if (mode === 'hybrid') {
                    handleHybridRegistration({
                        email: values.email,
                        first_name: values.first_name,
                        last_name: values.last_name,
                        company_name: values.company_name ?? '',
                    });
                    handleClose();
                } else if (mode === 'missing-details') {
                    setIsRegistrationLoading(true);
                    try {
                        await patchUserPrimaryDetails({
                            id: Number(eventId),
                            type: eventType as EventTypes,
                            guestAccessHash,
                            ...values,
                        }).unwrap();
                    } catch (err) {
                        if (isApiResponseError(err) && Array.isArray(err.data)) {
                            if (err.data[0]?.includes('seats available')) {
                                setFailedStatus(FailedStatus.MAX_CAPACITY);
                            } else if (err.data[0]?.includes(FailedStatus.LOGIN)) {
                                setFailedStatus(FailedStatus.LOGIN);
                            } else {
                                handleError(
                                    intl.formatMessage({
                                        id: 'event.error.registration',
                                        defaultMessage: 'Error registering for event',
                                    }),
                                    err
                                );
                            }
                            handleClose();
                            return;
                        }
                    }

                    if (isWaitingListFlow) {
                        // Call parent function with form values
                        await handleJoinWaitingList();
                        handleClose();
                    } else {
                        // Will registration include a questionnaire?
                        if (event?.enable_questionnaire) {
                            // Is the user re-registering? (User has been registered to this event before)
                            if (!!event.subscriber_status_updated_at) {
                                await handleRegister();
                            } else {
                                handleClose();
                                handleUnauthQuizz();
                            }
                        } else {
                            await handleRegister();
                        }
                    }
                }
            } catch (err) {
                handleError(
                    intl.formatMessage({
                        id: 'event.error.registration',
                        defaultMessage: 'Error registering for event',
                    }),
                    err
                );
            } finally {
                setIsRegistrationLoading(false);
            }
        },
    });

    const handleClose = () => {
        props.onClose?.({}, 'escapeKeyDown');
        setTimeout(() => {
            resetForm();
        }, 300);
    };

    return (
        <Dialog
            {...props}
            disableEscapeKeyDown
            onClose={handleClose}
            slotProps={{
                paper: {
                    className:
                        'flex rounded-2xl items-center pt-14 pb-8 px-14 min-w-0 md:min-w-[828px] sm:max-w-[828px] sm:px-12 md:overflow-hidden',
                },
            }}
        >
            <CloseButton className="size-10 flex" color="black" onClick={handleClose} />
            <DialogTitle className="w-full px-2 mx-auto text-center max-w-[523px]">
                <Typography className="mb-4" fontSize={32} fontFamily="museo-sans" letterSpacing={-0.8}>
                    <FormattedMessage id="event.missingDetails.title" defaultMessage="Fill in your information" />
                </Typography>
                <Typography className="mb-5 whitespace-break-spaces" fontSize={16} letterSpacing={0}>
                    <FormattedMessage
                        id="event.missingDetails.description"
                        defaultMessage="In order to get you on the guest list for the event you've been invited for, we need some information about you."
                    />
                </Typography>
                {mode === 'unauthenticated' || !user ? (
                    <Typography
                        className="w-full flex gap-1 justify-center whitespace-nowrap"
                        fontWeight={600}
                        fontSize={12}
                    >
                        <FormattedMessage
                            id="common.alreadyHaveAccount"
                            defaultMessage="Already have a Victron Professional account?"
                        />
                        <Typography
                            component={Link}
                            color="primary"
                            fontSize={12}
                            fontWeight={600}
                            className="cursor-pointer"
                            to={toLogin()}
                        >
                            <FormattedMessage id="common.logIn" defaultMessage="Log in" />
                        </Typography>
                    </Typography>
                ) : null}
            </DialogTitle>
            <DialogContent className="w-full p-0 mt-5 text-center sm:py-2">
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    <InputField
                        className="mt-2 md:mt-0"
                        label={intl.formatMessage({ id: 'common.firstName', defaultMessage: 'First Name' })}
                        fullWidth
                        name="first_name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.first_name && !!errors.first_name}
                        helperText={touched.first_name && errors.first_name}
                    />
                    <InputField
                        label={intl.formatMessage({ id: 'common.lastName', defaultMessage: 'Last Name' })}
                        fullWidth
                        name="last_name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.last_name && !!errors.last_name}
                        helperText={touched.last_name && errors.last_name}
                    />
                    <InputField
                        label={intl.formatMessage({ id: 'common.email', defaultMessage: 'Email' })}
                        fullWidth
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.email && !!errors.email}
                        helperText={touched.email && errors.email}
                    />
                    <InputField
                        label={intl.formatMessage({ id: 'common.company', defaultMessage: 'Company' })}
                        fullWidth
                        name="company_name"
                        value={values.company_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.company_name && !!errors.company_name}
                        helperText={touched.company_name && errors.company_name}
                    />
                    <Select
                        id="country_code"
                        searchable
                        name="country_code"
                        size="smallest"
                        label={intl.formatMessage({ id: 'common.country', defaultMessage: 'Country' })}
                        items={countries ?? []}
                        value={values.country_code}
                        onChange={handleChange}
                        type="radio"
                        error={touched.country_code && !!errors.country_code}
                        helperText={touched.country_code && errors.country_code && errors.country_code}
                        placeholder={intl.formatMessage({
                            id: 'common.fields.country.placeholder',
                            defaultMessage: 'Select your country',
                        })}
                        renderOption={renderCountryOption}
                        renderValue={(countryCode) => renderCountryValue(countryCode as string, countries || [])}
                        disabled={isCountriesLoading}
                        loading={isCountriesLoading}
                    />
                    <div className="flex flex-col gap-2 mt-5 md:gap-4 md:mb-6 md:flex-row">
                        <Button
                            fullWidth
                            color="neutral-650"
                            className="rounded-full"
                            startIcon={<CloseIcon />}
                            onClick={handleClose}
                            sx={{
                                border: '1px solid #00000029',
                            }}
                        >
                            <span>
                                <FormattedMessage id="common.goBack" defaultMessage="Go Back" />
                            </span>
                        </Button>
                        <Button
                            type="submit"
                            fullWidth
                            className="rounded-full"
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="white" /> : <CheckIcon />}
                            sx={{
                                border: '1px solid #00000029',
                            }}
                        >
                            <span>
                                <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
                            </span>
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export { UnauthenticatedFormDialog };
